import { useState } from 'react';
import { Spinner } from '@klaro7/ui';
import { RegistroRitenutaStato } from '@klaro7/shared-types';
import type { RegistroRitenuta } from '@klaro7/shared-types';

import { extractErrorMessage } from '@/lib/api';
import { useRegistro, useMarkVersata, useRunRollup, downloadCuCsv } from './api';

// ── Formatting helpers ────────────────────────────────────────────────────────

function formatEur(amount: number): string {
  return amount.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatMese(meseRiferimento: string): string {
  // meseRiferimento is expected to be "YYYY-MM" or ISO date
  const d = new Date(meseRiferimento);
  if (isNaN(d.getTime())) return meseRiferimento;
  return d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatoBadge({ stato }: { stato: RegistroRitenutaStato }) {
  if (stato === RegistroRitenutaStato.VERSATA) {
    return (
      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        Versata
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      Da versare
    </span>
  );
}

// ── Inline "Segna versata" form ───────────────────────────────────────────────

interface MarkVersataFormProps {
  row: RegistroRitenuta;
  onDismiss: () => void;
}

function MarkVersataForm({ row, onDismiss }: MarkVersataFormProps) {
  const [protocollo, setProtocollo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const markVersata = useMarkVersata();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!protocollo.trim()) {
      setError('Inserisci il numero protocollo F24');
      return;
    }
    setError(null);
    markVersata.mutate(
      { id: row.id, f24Protocollato: protocollo.trim() },
      {
        onSuccess: () => onDismiss(),
        onError: (err) => setError(extractErrorMessage(err)),
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-2">
      <div className="text-xs text-neutral-500">
        Ritenuta {formatEur(row.ritenutaVersare)} · Lordo {formatEur(row.lordoErogato)}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Protocollo F24"
          value={protocollo}
          onChange={(e) => setProtocollo(e.target.value)}
          className="h-8 flex-1 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-brand-teal/50 focus:outline-none"
        />
        <button
          type="submit"
          disabled={markVersata.isPending}
          className="h-8 rounded-lg bg-brand-teal px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-teal/90 disabled:opacity-50"
        >
          {markVersata.isPending ? <Spinner size="sm" /> : 'Conferma'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={markVersata.isPending}
          className="h-8 rounded-lg border border-neutral-200 px-3 text-xs text-neutral-600 transition-colors hover:bg-neutral-50 disabled:opacity-50"
        >
          Annulla
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </form>
  );
}

// ── CU Export block ───────────────────────────────────────────────────────────

function CuExportBlock() {
  const currentYear = new Date().getFullYear();
  const [cuYear, setCuYear] = useState(currentYear);
  const [downloading, setDownloading] = useState(false);
  const [cuError, setCuError] = useState<string | null>(null);

  const handleDownload = async () => {
    setCuError(null);
    setDownloading(true);
    try {
      await downloadCuCsv(cuYear);
    } catch (err) {
      setCuError(extractErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6">
      <h2 className="mb-1 text-base font-semibold text-neutral-900">
        Esporta Certificazione Unica (CU)
      </h2>
      <p className="mb-4 text-sm text-neutral-500">
        Scarica il CSV annuale da consegnare al commercialista.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label
            htmlFor="cuYear"
            className="mb-1 block text-sm font-medium text-neutral-700"
          >
            Anno
          </label>
          <input
            id="cuYear"
            type="number"
            min={2020}
            max={currentYear}
            value={cuYear}
            onChange={(e) => setCuYear(Number(e.target.value))}
            className="h-9 w-28 rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
          />
        </div>
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={downloading}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-teal px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-teal/90 disabled:opacity-60"
        >
          {downloading ? (
            <Spinner size="sm" />
          ) : (
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
          )}
          Scarica CU (CSV)
        </button>
      </div>
      {cuError && (
        <p className="mt-2 text-sm text-red-600">
          Impossibile scaricare il file CU: {cuError}
        </p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function F24RegisterPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { data: rows, isLoading, isError } = useRegistro(year, month);
  const runRollup = useRunRollup();

  // Which row id currently shows the mark-versata form
  const [markOpenId, setMarkOpenId] = useState<string | null>(null);
  const [rollupError, setRollupError] = useState<string | null>(null);
  const [rollupSuccess, setRollupSuccess] = useState(false);

  const handleRollup = () => {
    setRollupError(null);
    setRollupSuccess(false);
    runRollup.mutate(
      { year, month },
      {
        onSuccess: () => setRollupSuccess(true),
        onError: (err) => setRollupError(extractErrorMessage(err)),
      }
    );
  };

  const totalDaVersare = (rows ?? [])
    .filter((r) => r.stato === RegistroRitenutaStato.DA_VERSARE)
    .reduce((acc, r) => acc + r.ritenutaVersare, 0);

  const currentYear = now.getFullYear();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Ritenute / F24</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Registro mensile ritenute d'acconto (sostituto d'imposta). Codice tributo 1040 —
          versare entro il 16 del mese successivo.
        </p>
      </div>

      {/* Period selector + rollup */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label
              htmlFor="regYear"
              className="mb-1 block text-sm font-medium text-neutral-700"
            >
              Anno
            </label>
            <input
              id="regYear"
              type="number"
              min={2020}
              max={currentYear}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 w-28 rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
            />
          </div>
          <div>
            <label
              htmlFor="regMonth"
              className="mb-1 block text-sm font-medium text-neutral-700"
            >
              Mese
            </label>
            <select
              id="regMonth"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
            >
              {[
                'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
              ].map((name, i) => (
                <option key={i + 1} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={handleRollup}
              disabled={runRollup.isPending}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-orange/90 disabled:opacity-60"
            >
              {runRollup.isPending ? (
                <>
                  <Spinner size="sm" />
                  Calcolo…
                </>
              ) : rows && rows.length > 0 ? (
                'Ricalcola'
              ) : (
                'Genera registro'
              )}
            </button>
            {rollupError && <p className="text-xs text-red-600">{rollupError}</p>}
            {rollupSuccess && (
              <p className="text-xs text-green-600">Registro aggiornato.</p>
            )}
          </div>
        </div>
      </div>

      {/* Registro table */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <div className="px-5 py-8 text-center text-sm text-red-600">
            Impossibile caricare il registro. Riprova più tardi.
          </div>
        ) : !rows || rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
              <svg
                className="h-6 w-6 text-neutral-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                />
              </svg>
            </div>
            <p className="mt-3 text-sm font-medium text-neutral-700">
              Nessuna riga per questo periodo
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              Clicca "Genera registro" per aggregare i payout pagati del mese.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 bg-neutral-50 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                    <th className="px-5 py-3.5 text-left">Professionista</th>
                    <th className="px-5 py-3.5 text-left">Mese rif.</th>
                    <th className="px-5 py-3.5 text-right">Lordo erogato</th>
                    <th className="px-5 py-3.5 text-right">Ritenuta da versare</th>
                    <th className="px-5 py-3.5 text-center">Stato</th>
                    <th className="px-5 py-3.5 text-left">Data versamento</th>
                    <th className="px-5 py-3.5 text-left">Protocollo F24</th>
                    <th className="px-5 py-3.5 text-left">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-neutral-50/50">
                      <td className="px-5 py-3.5">
                        {/* Nominativo in primo piano, identificativo interno sotto:
                            chi compila l'F24 cerca il nome, non l'ObjectId. */}
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium text-neutral-900">
                            {`${row.professionalFirstName} ${row.professionalLastName}`.trim() || 'Professionista'}
                          </span>
                          <span className="font-mono text-[11px] text-neutral-400">
                            {row.professionalId}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-neutral-700">
                        {formatMese(row.meseRiferimento)}
                      </td>
                      <td className="px-5 py-3.5 text-right font-medium text-neutral-900">
                        {formatEur(row.lordoErogato)}
                      </td>
                      <td className="px-5 py-3.5 text-right font-semibold text-neutral-900">
                        {formatEur(row.ritenutaVersare)}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <StatoBadge stato={row.stato} />
                      </td>
                      <td className="px-5 py-3.5 text-neutral-600">
                        {formatDate(row.dataVersamento)}
                      </td>
                      <td className="px-5 py-3.5 text-neutral-600">
                        {row.f24Protocollato ?? '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        {row.stato === RegistroRitenutaStato.DA_VERSARE ? (
                          markOpenId === row.id ? (
                            <MarkVersataForm
                              row={row}
                              onDismiss={() => setMarkOpenId(null)}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => setMarkOpenId(row.id)}
                              className="rounded-lg bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-orange/90"
                            >
                              Segna versata
                            </button>
                          )
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary line */}
            <div className="border-t border-neutral-100 bg-neutral-50 px-5 py-4">
              <p className="text-sm text-neutral-700">
                <span className="font-semibold">Totale ritenuta da versare: </span>
                <span className="font-bold text-neutral-900">{formatEur(totalDaVersare)}</span>
                {' '}
                <span className="text-neutral-500">
                  — Codice tributo 1040 · versare entro il 16 del mese successivo
                </span>
              </p>
            </div>
          </>
        )}
      </div>

      {/* CU export */}
      <CuExportBlock />
    </div>
  );
}
